// packages/core/src/quizzes/content/sameGame.ts
// ─────────────────────────────────────────────────────────────────────────
// "Are You Playing the Same Game?" (tradeoff engine, two-player)
//
// Ten binary questions, five axes, each axis appearing in exactly four
// questions (max score 8, a consistent picker always wins their axis).
// Both partners answer the same ten questions separately; the result is
// the COMPARISON, never a solo verdict. Player one sees no result until
// player two finishes, which keeps the reveal honest and un-anchored.
//
// Axis coverage matrix (question → option a / option b):
//   1 future/present  2 security/future  3 together/independence
//   4 present/security  5 together/future  6 independence/together
//   7 future/present  8 security/independence  9 present/together
//   10 security/independence
// ─────────────────────────────────────────────────────────────────────────

import type { TradeoffQuizContent } from '../DiscoveryTypes';

export const SAME_GAME: TradeoffQuizContent = {
	meta: {
		id: 'same-game',
		title: 'Are You Playing the Same Game?',
		hook: 'Ten questions, answered separately. Are you on the same page?',
		engine: 'tradeoff',
		minutes: 6,
		status: 'live',
		twoPlayer: true,
	},

	questions: [
		{
			type: 'tradeoff',
			id: 'sg-windfall',
			prompt: 'A $10,000 windfall lands. Your first instinct:',
			options: [
				{
					label: 'Straight into the future pile.',
					weights: { future: 2 },
				},
				{
					label: 'One unforgettable trip together, this year.',
					weights: { present: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-cushion',
			prompt: 'The emergency fund feels right at:',
			options: [
				{
					label: 'A full year of spending. Sleep matters.',
					weights: { security: 2 },
				},
				{
					label: 'A few months. Money sitting still bothers me.',
					weights: { future: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-pots',
			prompt: 'Money in a relationship should be:',
			options: [
				{
					label: 'Fully shared. One team, one pot.',
					weights: { together: 2 },
				},
				{
					label: 'Ours, with room that is only mine.',
					weights: { independence: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-this-year',
			prompt: 'Real money this year should go toward:',
			options: [
				{
					label: 'Living better right now. That is what it is for.',
					weights: { present: 2 },
				},
				{
					label: 'Thicker buffers. Calm is worth more than upgrades.',
					weights: { security: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-home',
			prompt: 'The bigger dream:',
			options: [
				{
					label: 'Buying the home we grow old in, soon.',
					weights: { together: 2 },
				},
				{
					label: 'Renting light and investing the difference.',
					weights: { future: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-accounts',
			prompt: 'Day to day, our accounts should work like:',
			options: [
				{
					label: 'Separate fun money, no questions asked.',
					weights: { independence: 2 },
				},
				{
					label: 'Every dollar visible to both of us.',
					weights: { together: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-retire',
			prompt: 'Given the choice:',
			options: [
				{
					label: 'Retire 10 years early by living lean now.',
					weights: { future: 2 },
				},
				{
					label: 'Retire on time with fuller years along the way.',
					weights: { present: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-careers',
			prompt: 'Between our two careers:',
			options: [
				{
					label: 'One steady anchor keeps the household safe.',
					weights: { security: 2 },
				},
				{
					label: 'Both of us stay free to bet on risky work.',
					weights: { independence: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-spending-joy',
			prompt: 'The spending that feels most worth it:',
			options: [
				{
					label: 'Experiences designed for just the two of us.',
					weights: { present: 2 },
				},
				{
					label: 'Gathering everyone we love, on us.',
					weights: { together: 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sg-enough',
			prompt: '"Enough money" means:',
			options: [
				{
					label: 'Never having to worry again.',
					weights: { security: 2 },
				},
				{
					label: 'Never having to ask permission again.',
					weights: { independence: 2 },
				},
			],
		},
	],

	axes: {
		security: {
			label: 'Safety First',
			insight:
				'For you, money\u2019s first job is making sure nothing can knock the two of you over. Buffers before upgrades, calm before speed.',
			blindSpot:
				'"Safe enough" measured by feel keeps moving, and every extra layer of padding is bought with something the two of you wanted now.',
		},
		present: {
			label: 'Live It Now',
			insight:
				'For you, money\u2019s first job is funding a life worth having while you are both young enough to have it. The years are the scarce asset.',
			blindSpot:
				'Every permanent habit sets the size of the future you will have to fund together. The trade is honest only when it is priced out loud.',
		},
		future: {
			label: 'Build the Pile',
			insight:
				'For you, money\u2019s first job is compounding. Every dollar working today is a bigger set of choices for the two of you later.',
			blindSpot:
				'A pile with no spending plan becomes the goal instead of the tool, and "later" has a way of staying later forever.',
		},
		together: {
			label: 'One Team',
			insight:
				'For you, money is a shared project. One pot, one plan, decisions made out loud. Merging is the point, not the paperwork.',
			blindSpot:
				'Full merging without any private room can turn every small purchase into a negotiation, which is its own quiet tax.',
		},
		independence: {
			label: 'Room of Your Own',
			insight:
				'For you, love does not require an audit trail. Shared goals, yes, but with space that belongs to each of you alone.',
			blindSpot:
				'Too much separation hides the full picture, and the big goals only compound when both engines are visibly pulling the same direction.',
		},
	},

	cta: {
		label: 'Run your numbers as a team',
		to: '/simulate',
		blurb: 'Different games are fine. Undiscussed games are expensive. Nisk turns your combined income, spending, and savings into one shared date you can both see.',
	},

	// Two-player share uses {matched} and {total} instead of {primary}.
	shareText:
		'We matched on {matched} of {total} money questions on Nisk\u2019s Same Game test for couples. Find out what game you two are playing: https://nisk.co/quizzes/same-game',

	twoPlayer: {
		invitePrompt:
			'Answer the ten questions first. Then send your partner a link. They answer without seeing your picks, and the comparison unlocks for both of you.',
		inviteHint:
			'The link carries only your answer choices. No names, no account, nothing else.',
		waitingTitle: 'Your half is locked in',
		waitingNote:
			'No peeking at a solo result. The reveal only works when both halves exist. Send your partner the link, and when they finish they can send the comparison back to you.',
		partnerIntro:
			'Your partner already answered these ten questions. Your turn. You will not see their picks until you finish, and neither of you can change answers after.',
		alignedCopy:
			'Same game. You two are optimizing for the same thing, which makes the money conversations tactics, not treaties. The disagreements you do have are about the how, not the what, and those are the cheap kind.',
		splitCopy:
			'Different games. Neither one is wrong, but unnamed, this difference dresses up as fights about groceries and vacations that are actually about this. Now it has a name, which is most of the fix.',
	},
};
